import React from "react";
import { format } from "date-fns";
import {
  Card,
  DataTable,
  KeyValueList,
  LongTextLabel,
  NLinesShowMore,
  Table,
  TextWithStatus,
  ValueOrPlaceholder,
} from "@sscs/ui";
import { CheckIcon as OutcomeSuccessful } from "@sscs/ui/icons";
import {
  DATE_FIELD_FORMAT,
  TIME_FIELD_FORMAT,
  capitalizeFirstLetter,
} from "@sscs/utils";
import { AUDIT_RESOURCE_TYPES, AuditLog } from "../../types";
import { transformValue } from "../../utils";
import styles from "./auditLog.module.scss";
import UserInfo from "./UserInfo";

type AuditLogProps = {
  log: AuditLog;
  focused: boolean;
  onClick: () => void;
};

const AuditLog = ({
  log,
  focused = false,
  onClick,
}: AuditLogProps): React.JSX.Element => {
  return (
    <DataTable.ExpandedRow focused={focused} onClick={onClick}>
      <DataTable.ExpandedRow.Summary>
        <Table.Cell>
          <TextWithStatus text={format(log.timestamp, TIME_FIELD_FORMAT)}>
            {format(log.timestamp, DATE_FIELD_FORMAT)}
          </TextWithStatus>
        </Table.Cell>
        <Table.Cell>
          <UserInfo log={log} />
        </Table.Cell>
        <Table.Cell>{log.action}</Table.Cell>
        <Table.Cell>
          <TextWithStatus
            text={
              <LongTextLabel
                label={<ValueOrPlaceholder value={log.resource_name} />}
                title={log.resource_name || ""}
              />
            }
          >
            {AUDIT_RESOURCE_TYPES[log.resource_type]}
          </TextWithStatus>
        </Table.Cell>
        <Table.Cell>
          {log.outcome === "FAILURE" ? "Unsuccessful" : <OutcomeSuccessful />}
        </Table.Cell>
      </DataTable.ExpandedRow.Summary>
      <DataTable.ExpandedRow.Details>
        <Card.Card>
          <Card.Title>
            <span>Additional information</span>
          </Card.Title>
          <Card.Content>
            <KeyValueList
              list={[
                {
                  ...(log.user_id && { "User ID": log.user_id }),
                  ...(log.user_email && { "User email": log.user_email }),
                  Action: log.action,
                  Outcome: log.outcome,
                  ...(log.group_id && { "Group ID": log.group_id }),
                  ...(log.project_id && { "Project ID": log.project_id }),
                  ...(log.project_name && { "Project name": log.project_name }),
                  ...(log.package_id && { "Package ID": log.package_id }),
                  ...(log.package_name && { "Package name": log.package_name }),
                  "Resource type": AUDIT_RESOURCE_TYPES[log.resource_type],
                  ...(log.resource_id && { "Resource ID": log.resource_id }),
                  ...(log.resource_name && {
                    "Resource name": log.resource_name,
                  }),
                  ...(log.ip_address && { "IP address": log.ip_address }),
                  ...(log.user_agent_raw && {
                    "User agent": log.user_agent_raw,
                  }),
                  ...(log.user_agent_category && {
                    "User agent category": log.user_agent_category,
                  }),
                  ...(log.auth_context && {
                    "Authentication context": capitalizeFirstLetter(
                      log.auth_context.toLowerCase(),
                    ),
                  }),
                  ...(log.request_path && { "Request path": log.request_path }),
                  ...(log.request_id && { "Request ID": log.request_id }),
                },
                ...(log.error ? ["Error", log.error] : []),
                ...(log.changes
                  ? [
                      "Changes",
                      Object.entries(log.changes).reduce<
                        Record<string, React.ReactNode>
                      >((acc, [change, values]) => {
                        acc[
                          change.endsWith("_bytes")
                            ? change.slice(0, -6)
                            : change
                        ] = (
                          <div className={styles["change-container"]}>
                            <div className={styles.change}>
                              <NLinesShowMore
                                value={
                                  <ValueOrPlaceholder
                                    value={transformValue(change, values.old)}
                                  />
                                }
                                numberOfLines={3}
                              />
                            </div>
                            <span>&rarr;</span>
                            <div className={styles.change}>
                              <NLinesShowMore
                                value={
                                  <ValueOrPlaceholder
                                    value={transformValue(change, values.new)}
                                  />
                                }
                                numberOfLines={3}
                              />
                            </div>
                          </div>
                        );
                        return acc;
                      }, {}),
                    ]
                  : []),
                ...(log.context
                  ? [
                      "Context",
                      Object.fromEntries(
                        Object.entries(log.context).map(
                          ([key, value]): [string, string] => {
                            return [
                              key.endsWith("_bytes") ? key.slice(0, -6) : key,
                              transformValue(key, value) ?? "",
                            ];
                          },
                        ),
                      ),
                    ]
                  : []),
              ]}
            />
          </Card.Content>
        </Card.Card>
      </DataTable.ExpandedRow.Details>
    </DataTable.ExpandedRow>
  );
};

export default AuditLog;
